import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertCircle, ExternalLink, Loader2, Sheet } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSetup = () => {
  const [sheetsStatus, setSheetsStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);

  useEffect(() => {
    checkSheetsStatus();
    
    // Check if we're returning from OAuth
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('sheets_connected') === 'true') {
      toast.success('Google Sheets connected successfully!');
      window.history.replaceState({}, '', '/admin/setup');
    } else if (urlParams.get('sheets_error')) {
      toast.error(`Failed to connect: ${urlParams.get('sheets_error')}`);
      window.history.replaceState({}, '', '/admin/setup');
    }
  }, []);

  const checkSheetsStatus = async () => {
    try {
      const response = await fetch(`${API}/admin/sheets-status`);
      const data = await response.json();
      setSheetsStatus(data);
    } catch (error) {
      console.error('Error checking sheets status:', error);
      setSheetsStatus({ connected: false, error: 'Could not check status' });
    } finally {
      setIsLoading(false);
    }
  };

  const connectGoogleSheets = async () => {
    setIsConnecting(true);
    try {
      const response = await fetch(`${API}/oauth/sheets/login`);
      const data = await response.json();
      
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      } else {
        toast.error('Could not initiate Google authorization');
        setIsConnecting(false);
      }
    } catch (error) {
      console.error('Error connecting to Google Sheets:', error);
      toast.error('Failed to connect to Google Sheets');
      setIsConnecting(false);
    }
  };

  const disconnectGoogleSheets = async () => {
    try {
      const response = await fetch(`${API}/admin/sheets-disconnect`, {
        method: 'POST'
      });
      
      if (response.ok) {
        toast.success('Google Sheets disconnected');
        setSheetsStatus({ connected: false });
      }
    } catch (error) {
      console.error('Error disconnecting:', error);
      toast.error('Failed to disconnect');
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary)' }}>
      {/* Header */}
      <header style={{ background: 'var(--text-primary)', padding: '1rem 0' }}>
        <div className="container">
          <div className="flex items-center justify-between">
            <a href="/" className="text-white font-bold text-xl">Curis Path</a>
            <span className="text-white/70 text-sm">Admin Setup</span>
          </div>
        </div>
      </header>

      <div className="container py-12">
        <div className="max-w-2xl mx-auto">
          <h1 className="heading-1 mb-2">Admin Setup</h1>
          <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>
            Configure integrations for your Curis Path website
          </p>

          {/* Google Sheets Integration Card */}
          <div className="voice-card accent-grey p-6 mb-6">
            <div className="flex items-start gap-4">
              <div 
                className="w-12 h-12 rounded-lg flex items-center justify-center"
                style={{ background: '#0F9D58' }}
              >
                <Sheet className="text-white" size={24} />
              </div>
              
              <div className="flex-1">
                <h2 className="heading-3 mb-1">Google Sheets Integration</h2>
                <p className="body-small mb-4" style={{ color: 'var(--text-secondary)' }}>
                  Sync patient enquiries to your Google Sheet automatically
                </p>

                {isLoading ? (
                  <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                    <Loader2 className="animate-spin" size={16} />
                    Checking status...
                  </div>
                ) : sheetsStatus?.connected ? (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle className="text-green-500" size={18} />
                      <span className="text-green-600 font-medium">Connected</span>
                    </div>
                    
                    {sheetsStatus.sheet_id && (
                      <a
                        href={`https://docs.google.com/spreadsheets/d/${sheetsStatus.sheet_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm mb-4 hover:underline"
                        style={{ color: 'var(--accent-primary)' }}
                      >
                        View Google Sheet <ExternalLink size={14} />
                      </a>
                    )}
                    
                    <div className="flex gap-3">
                      <button
                        onClick={disconnectGoogleSheets}
                        className="px-4 py-2 rounded-lg text-sm font-medium border"
                        style={{ 
                          borderColor: 'var(--border-light)',
                          color: 'var(--text-secondary)'
                        }}
                      >
                        Disconnect
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <AlertCircle className="text-amber-500" size={18} />
                      <span className="text-amber-600 font-medium">Not Connected</span>
                    </div>
                    
                    {sheetsStatus?.needs_credentials ? (
                      <div className="p-4 rounded-lg mb-4" style={{ background: 'var(--bg-section)' }}>
                        <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
                          To connect Google Sheets, you need to set up Google OAuth credentials:
                        </p>
                        <ol className="text-sm space-y-2" style={{ color: 'var(--text-secondary)' }}>
                          <li>1. Go to <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console</a></li>
                          <li>2. Create a project and enable Google Sheets API</li>
                          <li>3. Create OAuth 2.0 credentials</li>
                          <li>4. Add credentials to environment variables</li>
                        </ol>
                      </div>
                    ) : (
                      <button
                        onClick={connectGoogleSheets}
                        disabled={isConnecting}
                        className="btn-primary flex items-center gap-2"
                        style={{ opacity: isConnecting ? 0.7 : 1 }}
                        data-testid="connect-sheets-btn"
                      >
                        {isConnecting ? (
                          <>
                            <Loader2 className="animate-spin" size={18} />
                            Connecting...
                          </>
                        ) : (
                          <>
                            Connect Google Sheets
                          </>
                        )}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Info Card */}
          <div 
            className="p-4 rounded-lg"
            style={{ background: 'var(--bg-section)', border: '1px solid var(--border-light)' }}
          >
            <h3 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              How it works
            </h3>
            <ul className="text-sm space-y-1" style={{ color: 'var(--text-secondary)' }}>
              <li>• When a patient submits an enquiry, it's saved to MongoDB</li>
              <li>• If Google Sheets is connected, the data is also synced to your sheet</li>
              <li>• The sheet will have columns: Timestamp, Name, Phone, City, Country, Procedure, Reports, Timeframe</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSetup;
